/**
 * Jertscript Sarcastic Error Messages
 *
 * Error messages that mix French flair with CTO roasts.
 * "Honestly, TypeScript could use more croissants." 🥐
 */

/**
 * Error categories
 */
export enum ErrorCategory {
  SYNTAX = 'syntax',
  TYPE = 'type',
  REFERENCE = 'reference',
  RUNTIME = 'runtime',
  WARNING = 'warning',
}

/**
 * Lexer-specific error types
 */
export enum LexerErrorType {
  UNTERMINATED_STRING = 'UNTERMINATED_STRING',
  UNTERMINATED_COMMENT = 'UNTERMINATED_COMMENT',
  INVALID_CHARACTER = 'INVALID_CHARACTER',
  UNEXPECTED_TOKEN = 'UNEXPECTED_TOKEN',
}

/**
 * Parser-specific error types
 */
export enum ParserErrorType {
  MISSING_SEMICOLON = 'MISSING_SEMICOLON',
  MISSING_CLOSING_PAREN = 'MISSING_CLOSING_PAREN',
  MISSING_CLOSING_BRACE = 'MISSING_CLOSING_BRACE',
  INVALID_ASSIGNMENT_TARGET = 'INVALID_ASSIGNMENT_TARGET',
  EXPECTED_EXPRESSION = 'EXPECTED_EXPRESSION',
  EXPECTED_IDENTIFIER = 'EXPECTED_IDENTIFIER',
  EXPECTED_FUNCTION_NAME = 'EXPECTED_FUNCTION_NAME',
  EXPECTED_PARAMETER_NAME = 'EXPECTED_PARAMETER_NAME',
  EXPECTED_OPENING_PAREN = 'EXPECTED_OPENING_PAREN',
  EXPECTED_OPENING_BRACE = 'EXPECTED_OPENING_BRACE',
  UNKNOWN_STATEMENT = 'UNKNOWN_STATEMENT',
}

/**
 * Type-specific error types
 */
export enum TypeErrorType {
  TYPE_MISMATCH = 'TYPE_MISMATCH',
  UNDEFINED_VARIABLE = 'UNDEFINED_VARIABLE',
  NOT_A_FUNCTION = 'NOT_A_FUNCTION',
  WRONG_ARGUMENT_COUNT = 'WRONG_ARGUMENT_COUNT',
  CANNOT_ASSIGN_TO_CONST = 'CANNOT_ASSIGN_TO_CONST',
  INVALID_OPERATION = 'INVALID_OPERATION',
  NOT_AN_ARRAY = 'NOT_AN_ARRAY',
  NOT_AN_OBJECT = 'NOT_AN_OBJECT',
  PROPERTY_NOT_FOUND = 'PROPERTY_NOT_FOUND',
  INVALID_INDEX = 'INVALID_INDEX',
  NULL_REFERENCE = 'NULL_REFERENCE',
  INVALID_RETURN_TYPE = 'INVALID_RETURN_TYPE',
  MISSING_RETURN = 'MISSING_RETURN',
  INVALID_OPERAND = 'INVALID_OPERAND',
  CANNOT_ITERATE = 'CANNOT_ITERATE',
}

/**
 * Runtime-specific error types
 */
export enum RuntimeErrorType {
  DIVISION_BY_ZERO = 'DIVISION_BY_ZERO',
  STACK_OVERFLOW = 'STACK_OVERFLOW',
  INFINITE_LOOP = 'INFINITE_LOOP',
  OUT_OF_MEMORY = 'OUT_OF_MEMORY',
  TIMEOUT = 'TIMEOUT',
  ASSERTION_FAILED = 'ASSERTION_FAILED',
  UNHANDLED_EXCEPTION = 'UNHANDLED_EXCEPTION',
  INDEX_OUT_OF_BOUNDS = 'INDEX_OUT_OF_BOUNDS',
  RECURSION_LIMIT = 'RECURSION_LIMIT',
  ASYNC_ERROR = 'ASYNC_ERROR',
}

/**
 * Warning types
 */
export enum WarningType {
  UNUSED_VARIABLE = 'UNUSED_VARIABLE',
  UNUSED_FUNCTION = 'UNUSED_FUNCTION',
  UNREACHABLE_CODE = 'UNREACHABLE_CODE',
  DEPRECATED_FEATURE = 'DEPRECATED_FEATURE',
  SHADOWED_VARIABLE = 'SHADOWED_VARIABLE',
  MISSING_AWAIT = 'MISSING_AWAIT',
  IMPLICIT_ANY = 'IMPLICIT_ANY',
  LOOSE_EQUALITY = 'LOOSE_EQUALITY',
  EMPTY_BLOCK = 'EMPTY_BLOCK',
  SUSPICIOUS_ASSIGNMENT = 'SUSPICIOUS_ASSIGNMENT',
}

/**
 * Sarcastic error messages for unterminated strings
 */
const UNTERMINATED_STRING_MESSAGES = [
  "Sacre bleu! You started a string but never finished it. Much like your side projects.",
  "Mon ami, this string has no ending. Unlike French work weeks, strings should actually finish.",
  "Error: Unterminated string. Did you write this after leg day? Your quotes are as shaky as your legs.",
  "This string goes on forever, like the CTO's photo library of blurry snowboard shots.",
];

/**
 * Sarcastic error messages for unterminated comments
 */
const UNTERMINATED_COMMENT_MESSAGES = [
  "Sacre bleu! You started a comment but never finished it. Much like your side projects.",
  "This comment has no end. Unlike the CTO's ski pass collection, comments should have limits.",
  "Mon ami, close your comments! This isn't a stream of consciousness novel about powder days.",
];

/**
 * Sarcastic error messages for invalid characters
 */
const INVALID_CHARACTER_MESSAGES = [
  "Mon ami, what is this character '{char}'? Even the CTO's blurry photos are more readable.",
  "Error: Invalid character '{char}'. Too much pre-workout, not enough syntax knowledge.",
  "Sacre bleu! '{char}' is not valid Jertscript. It has the same energy as blurry snowboard photos - nonsensical.",
  "This character '{char}' is as unexpected as a non-blurry action shot from the CTO.",
];

/**
 * Sarcastic error messages for unexpected tokens
 */
const UNEXPECTED_TOKEN_MESSAGES = [
  "Error: Too much pre-workout, not enough syntax knowledge.",
  "Unexpected token. C'est la vie, but also c'est wrong.",
  "Mon ami, this is not how Jertscript works. Even the CTO knows this.",
  "Error: Your code has the same composition as parking lot photography - completely wrong.",
];

// ===========================================================================
// Parser Error Messages
// ===========================================================================

/**
 * Sarcastic error messages for missing semicolons
 */
const MISSING_SEMICOLON_MESSAGES = [
  "Ah oui oui, tres original. You forgot a semicolon. Did you write this after leg day?",
  "Sacre bleu! Missing semicolon. Your logic is as shaky as your legs after squats.",
  "Error: Expected ';' but found disappointment. Much like the CTO's snowboard landing photos.",
  "Mon ami, semicolons are not optional. Unlike the CTO's focus when taking photos.",
];

/**
 * Sarcastic error messages for missing closing parenthesis
 */
const MISSING_CLOSING_PAREN_MESSAGES = [
  "You opened a parenthesis but never closed it. Unlike your gym sessions, code should eventually end.",
  "Missing ')'. This code has the same energy as an unfinished story about powder days.",
  "Sacre bleu! Close your parentheses! Even the CTO closes his camera lens... sometimes.",
  "Error: Expected ')'. Your parentheses are as unbalanced as the CTO's work-ski-life ratio.",
];

/**
 * Sarcastic error messages for missing closing brace
 */
const MISSING_CLOSING_BRACE_MESSAGES = [
  "This code has the same energy as blurry snowboard photos - incomplete. Expected '}'.",
  "Expected '{' but got nothing. Much like the ROI on that photography equipment.",
  "Mon ami, where is your closing brace? It's as missing as sharp focus in action shots.",
  "Sacre bleu! Missing '}'. Your code structure is as unstable as a beginner on a black diamond.",
];

/**
 * Sarcastic error messages for invalid assignment target
 */
const INVALID_ASSIGNMENT_TARGET_MESSAGES = [
  "You cannot assign to a croissant. It is immutable, like French pride.",
  "Invalid assignment target. You can't assign to this, just like you can't assign good composition to a parking lot photo.",
  "Sacre bleu! This is not assignable. Unlike ski passes, which the CTO hoards freely.",
  "Error: Invalid assignment. Only variables can be assigned, not... whatever this is.",
];

/**
 * Sarcastic error messages for expected expression
 */
const EXPECTED_EXPRESSION_MESSAGES = [
  "Expected an expression but got... whatever this is. Classic powder day brain.",
  "Error: Expected an expression. I've seen better code composition than this. Actually, I've seen better snowboard photos too.",
  "Mon ami, where is your expression? It's as absent as the CTO during ski season.",
  "Sacre bleu! Expected expression, got confusion. Like trying to understand blurry action shots.",
];

/**
 * Sarcastic error messages for expected identifier
 */
const EXPECTED_IDENTIFIER_MESSAGES = [
  "Sacre bleu! Expected a variable name after '{keyword}'.",
  "Error: Expected identifier. Try naming things better than 'epic_shot_final_v2_FINAL'.",
  "Mon ami, I need a name here. Something more creative than the CTO's photo filenames.",
  "Expected identifier but got... this. C'est tragique.",
];

/**
 * Sarcastic error messages for expected function name
 */
const EXPECTED_FUNCTION_NAME_MESSAGES = [
  "Mon ami, this is not how functions work. Too much pre-workout, not enough documentation.",
  "Error: Expected function name. Functions need names, unlike the CTO's thousands of unnamed photo files.",
  "Sacre bleu! Your function needs a name. 'fonction' alone is like a ski trip with no snow.",
  "Expected function name but got nothing. Much like expecting good photos from action shots.",
];

/**
 * Sarcastic error messages for expected parameter name
 */
const EXPECTED_PARAMETER_NAME_MESSAGES = [
  "Expected parameter name. Parameters need names, like how photos need focus.",
  "Sacre bleu! Missing parameter name. Even the CTO names his ski passes.",
  "Error: Expected parameter name here. This function signature is as unclear as a motion-blurred photo.",
  "Mon ami, your parameters are nameless. Unlike the CTO's extensive lens collection.",
];

/**
 * Sarcastic error messages for expected opening parenthesis
 */
const EXPECTED_OPENING_PAREN_MESSAGES = [
  "Expected '(' after '{name}'. Syntax matters, unlike composition in parking lot photos.",
  "Sacre bleu! Missing '('. Your code is as incomplete as the CTO's photo organization system.",
  "Error: Expected '(' but didn't find one. Like expecting sharp photos from a moving chairlift.",
  "Mon ami, where is your opening parenthesis? It's as missing as the CTO during powder days.",
];

/**
 * Sarcastic error messages for expected opening brace
 */
const EXPECTED_OPENING_BRACE_MESSAGES = [
  "Expected '{' before function body. Structure matters, unlike in snowboard selfies.",
  "Sacre bleu! Missing '{'. Your code structure is as chaotic as the CTO's photo library.",
  "Error: Expected '{'. This code needs proper structure, unlike casual powder day schedules.",
  "Mon ami, I need a '{' here. Code blocks are important, even if ski blocks aren't.",
];

/**
 * Sarcastic error messages for unknown statement
 */
const UNKNOWN_STATEMENT_MESSAGES = [
  "Mon ami, this is not how statements work. Even the CTO knows this.",
  "Error: Unknown statement. This syntax is worse than English pronunciation of French words.",
  "Sacre bleu! I don't recognize this statement. It's as foreign as good lighting in action shots.",
  "Unknown statement type. Your code has the same energy as claiming to be a 'pro' snowboarder.",
];

// ===========================================================================
// Type Error Messages
// ===========================================================================

/**
 * Sarcastic error messages for type mismatch
 */
const TYPE_MISMATCH_MESSAGES = [
  "TypeError: Expected a baguette but received... whatever this is. Sacre bleu!",
  "Mon ami, these types don't match. It's like comparing croissants to donuts.",
  "TypeError: Your types are as incompatible as the CTO's schedule with actual work.",
  "Sacre bleu! Type mismatch. You promised '{expected}' but delivered '{actual}'. Classic.",
];

/**
 * Sarcastic error messages for undefined variable
 */
const UNDEFINED_VARIABLE_MESSAGES = [
  "ReferenceError: '{name}' is not defined. C'est la vie. Maybe try better naming, like 'epic_shot'?",
  "'{name}' does not exist. I've seen better variable declarations than this. Actually, I've seen better snowboard photos too.",
  "Sacre bleu! '{name}' is as undefined as the CTO's workout schedule.",
  "Error: '{name}' is not defined. Like trying to find a non-blurry action shot in the CTO's camera roll.",
];

/**
 * Sarcastic error messages for not a function
 */
const NOT_A_FUNCTION_MESSAGES = [
  "TypeError: '{name}' is not a function. It has as much functionality as a lens cap.",
  "Mon ami, you cannot call '{name}' because it's not a function. Unlike my ability to roast the CTO.",
  "Sacre bleu! '{name}' is not callable. It's as useless as a ski pass in summer.",
  "Error: Tried to call '{name}' but it's not a function. Much like calling the CTO 'productive'.",
];

/**
 * Sarcastic error messages for wrong argument count
 */
const WRONG_ARGUMENT_COUNT_MESSAGES = [
  "Error: Function expected {expected} arguments but got {actual}. Math is hard after pre-workout.",
  "Sacre bleu! Wrong number of arguments. Expected {expected}, got {actual}. Like expecting one ski trip and getting twelve.",
  "Mon ami, {expected} arguments required, {actual} provided. Count like the CTO counts his camera lenses.",
  "TypeError: Argument count mismatch. Your function calls are as imprecise as action shot focus.",
];

/**
 * Sarcastic error messages for cannot assign to const
 */
const CANNOT_ASSIGN_TO_CONST_MESSAGES = [
  "You cannot assign to a croissant. It is immutable, like French pride and the CTO's commitment to ski passes.",
  "Error: Cannot reassign constant. A croissant once declared stays declared, unlike the CTO's gym schedule.",
  "Sacre bleu! This croissant is immutable. You cannot change it, like you cannot change blurry photos to sharp ones.",
  "Mon ami, constants are forever. Like the CTO's collection of ski gear.",
];

/**
 * Sarcastic error messages for invalid operation
 */
const INVALID_OPERATION_MESSAGES = [
  "TypeError: Cannot perform '{operation}' on '{type}'. This makes less sense than pre-workout at midnight.",
  "Invalid operation. You cannot '{operation}' a '{type}'. Even the CTO knows basic type theory.",
  "Sacre bleu! '{operation}' is not valid for '{type}'. Like using a ski pole as a camera tripod.",
  "Error: Invalid operation '{operation}'. This code has the energy of photographing with the lens cap on.",
];

/**
 * Sarcastic error messages for not an array
 */
const NOT_AN_ARRAY_MESSAGES = [
  "TypeError: Expected an array but got '{type}'. Your data structures are as organized as the CTO's photo library.",
  "Mon ami, this is not an array. Like how the CTO's 'organized' folders aren't actually organized.",
  "Sacre bleu! Cannot iterate over '{type}'. It's as iterable as a single snowflake.",
  "Error: Not an array. Your code expects lists but gets chaos, like ski trip planning.",
];

/**
 * Sarcastic error messages for not an object
 */
const NOT_AN_OBJECT_MESSAGES = [
  "TypeError: Expected an object but got '{type}'. Objects have properties, unlike blurry photos.",
  "Mon ami, you cannot access properties on '{type}'. It's not an object, it's a disappointment.",
  "Sacre bleu! '{type}' is not an object. Like expecting composition in parking lot selfies.",
  "Error: Cannot access property on '{type}'. Your types are as confused as the CTO's gear priorities.",
];

/**
 * Sarcastic error messages for property not found
 */
const PROPERTY_NOT_FOUND_MESSAGES = [
  "Error: Property '{property}' does not exist on type '{type}'. Like looking for focus in an action shot.",
  "Sacre bleu! '{property}' not found. It's as missing as proper exposure in chairlift photos.",
  "Mon ami, '{type}' has no property '{property}'. Like the CTO's photos have no composition.",
  "TypeError: Cannot find '{property}'. It's as lost as the CTO during a work meeting.",
];

/**
 * Sarcastic error messages for invalid index
 */
const INVALID_INDEX_MESSAGES = [
  "Error: Index must be a number, not '{type}'. Array indices aren't interpretive, unlike the CTO's 'art'.",
  "Sacre bleu! Invalid index type '{type}'. Like using a ski pole to point at photos.",
  "Mon ami, arrays use numbers for indices, not '{type}'. Basic stuff, even by CTO standards.",
  "TypeError: Cannot index with '{type}'. Your indices are as random as the CTO's photo selection.",
];

/**
 * Sarcastic error messages for null reference
 */
const NULL_REFERENCE_MESSAGES = [
  "NullPointerException: Found rien where you promised something. Much like promised 'epic content' from ski trips.",
  "Error: Cannot read property of rien. Like trying to see details in an overexposed photo.",
  "Sacre bleu! Null reference. Your code promises much and delivers nothing, like ski trip photo albums.",
  "Mon ami, this value is rien. As empty as the CTO's to-do list during ski season.",
];

/**
 * Sarcastic error messages for invalid return type
 */
const INVALID_RETURN_TYPE_MESSAGES = [
  "TypeError: Function returns '{actual}' but promised '{expected}'. Classic overpromise, underdeliver.",
  "Your function returns undefined, much like the composition in those parking lot photos.",
  "Sacre bleu! Expected return type '{expected}', got '{actual}'. Like expecting sharp, getting blurry.",
  "Mon ami, return type mismatch. Your function lies more than ski trip distance claims.",
];

/**
 * Sarcastic error messages for missing return
 */
const MISSING_RETURN_MESSAGES = [
  "Error: Function must return a value but ends without one. Like a ski story without the 'epic part'.",
  "Sacre bleu! Missing return statement. Your function goes nowhere, like the CTO's composition skills.",
  "Mon ami, where is your return? It's as absent as focus in moving chairlift shots.",
  "TypeError: Expected return value. Your function ends abruptly, like a good ski run hitting a tree.",
];

/**
 * Sarcastic error messages for invalid operand
 */
const INVALID_OPERAND_MESSAGES = [
  "Error: Cannot apply operator '{operator}' to '{type}'. Even the CTO wouldn't try this.",
  "Sacre bleu! Invalid operand type '{type}' for '{operator}'. Math doesn't work this way.",
  "Mon ami, '{operator}' doesn't work on '{type}'. Like applying Instagram filters to blurry photos - still bad.",
  "TypeError: Invalid operand. Your operators are as misused as a ski pass on a work day.",
];

/**
 * Sarcastic error messages for cannot iterate
 */
const CANNOT_ITERATE_MESSAGES = [
  "Error: Cannot iterate over '{type}'. It's as loop-able as a single snowflake.",
  "Sacre bleu! '{type}' is not iterable. Like the CTO's gym sessions - singular, not plural.",
  "Mon ami, you cannot loop over '{type}'. Some things just don't repeat, unlike pre-workout consumption.",
  "TypeError: '{type}' has no iterator. It's as traversable as the CTO's photo organization system.",
];

// ===========================================================================
// Runtime Error Messages
// ===========================================================================

/**
 * Sarcastic error messages for division by zero
 */
const DIVISION_BY_ZERO_MESSAGES = [
  "Error: Division by zero. The universe doesn't allow this. Neither does Jertscript. Or mathematics.",
  "Sacre bleu! You divided by zero! Even the CTO knows you can't do that. Actually, maybe he doesn't.",
  "Mon ami, dividing by zero creates a black hole. Like the void where the CTO's focused photos should be.",
  "RuntimeError: Division by zero. Your math is as broken as the CTO's camera focus in motion.",
];

/**
 * Sarcastic error messages for stack overflow
 */
const STACK_OVERFLOW_MESSAGES = [
  "Stack overflow! Your recursion is deeper than the CTO's collection of unused camera gear.",
  "Error: Stack overflow. Your function calls itself more than the CTO mentions ski trips.",
  "Sacre bleu! Stack overflow. This recursion is as endless as blurry photo slideshows.",
  "Mon ami, stack overflow. Your code loops like the CTO's stories about that one powder day.",
];

/**
 * Sarcastic error messages for infinite loop
 */
const INFINITE_LOOP_MESSAGES = [
  "Infinite loop detected. Unlike French work weeks, code should actually finish. Also unlike the CTO's photo organizing.",
  "Error: Infinite loop. This is running longer than a pre-workout fueled coding session.",
  "Sacre bleu! Infinite loop. Your while condition is as permanently true as the CTO's love for ski passes.",
  "Mon ami, this loop never ends. Like the CTO's camera gear wishlist.",
];

/**
 * Sarcastic error messages for out of memory
 */
const OUT_OF_MEMORY_MESSAGES = [
  "MemoryError: This function is hoarding RAM like someone hoards ski passes and camera lenses.",
  "Out of memory! Your code consumes more resources than the CTO's photo storage.",
  "Sacre bleu! Memory exhausted. Your program is as bloated as the CTO's camera bag.",
  "Mon ami, no more memory. Your code accumulated more baggage than a ski trip.",
];

/**
 * Sarcastic error messages for timeout
 */
const TIMEOUT_MESSAGES = [
  "Timeout! This operation took longer than the CTO deciding which ski run to take.",
  "Error: Operation timed out. Even French bureaucracy moves faster than this code.",
  "Sacre bleu! Timeout. Your code is slower than the CTO's photo editing workflow.",
  "Mon ami, time is up. This ran longer than explaining why the photos are blurry.",
];

/**
 * Sarcastic error messages for assertion failed
 */
const ASSERTION_FAILED_MESSAGES = [
  "Assertion failed: Expected oui but got non. Much like expecting sharp photos from action shots.",
  "Error: Assertion failed. Your assumptions are as wrong as the CTO's exposure settings.",
  "Sacre bleu! Assertion failed. Reality doesn't match expectations, like ski trip photo quality.",
  "Mon ami, assertion failed. Your code lies to itself, like 'I'll organize my photos later'.",
];

/**
 * Sarcastic error messages for unhandled exception
 */
const UNHANDLED_EXCEPTION_MESSAGES = [
  "Your code crashed harder than a double black diamond attempt. Sacre bleu!",
  "Unhandled exception! Your error handling is as robust as the CTO's tripod in wind.",
  "Error: Uncaught exception. Your code catches errors like the CTO catches good lighting - it doesn't.",
  "Mon ami, unhandled exception. Your try-catch blocks are as absent as sharp focus in chairlift selfies.",
];

/**
 * Sarcastic error messages for index out of bounds
 */
const INDEX_OUT_OF_BOUNDS_MESSAGES = [
  "IndexError: Out of bounds. You reached beyond the array, like the CTO's skiing beyond his skill level.",
  "Sacre bleu! Index {index} out of bounds. Arrays have limits, unlike ski pass hoarding.",
  "Mon ami, index out of bounds. You can't access what doesn't exist, like good CTO action shots.",
  "Error: Out of bounds at index {index}. Your array access is as off-target as chairlift photography.",
];

/**
 * Sarcastic error messages for recursion limit
 */
const RECURSION_LIMIT_MESSAGES = [
  "Error: Maximum recursion depth exceeded. Your function nests deeper than the CTO's denial about photo quality.",
  "Sacre bleu! Recursion limit hit. Your code calls itself more than the CTO checks for new camera gear.",
  "Mon ami, too much recursion. Like recursively asking 'but did you get THE shot?' - endless.",
  "RuntimeError: Recursion limit. Your function is as self-referential as ski trip humble-brags.",
];

/**
 * Sarcastic error messages for async errors
 */
const ASYNC_ERROR_MESSAGES = [
  "Async error! Your promise rejected harder than the CTO's blurry photos get rejected from portfolios.",
  "Error: Unhandled promise rejection. Your async code fails like automatic focus on moving targets.",
  "Sacre bleu! Async operation failed. Your promises are as reliable as 'I'll edit these photos later'.",
  "Mon ami, async error. Your asynchronous code is as unpredictable as mountain weather.",
];

// ===========================================================================
// Warning Messages
// ===========================================================================

/**
 * Sarcastic warning messages for unused variable
 */
const UNUSED_VARIABLE_MESSAGES = [
  "Warning: Variable '{name}' is defined but never used. Classic CTO energy.",
  "Mon ami, '{name}' is unused. It's as purposeless as a lens cap during golden hour.",
  "Warning: '{name}' declared but never used. Like the CTO's gym membership in ski season.",
  "Sacre bleu! '{name}' is just taking up space, like unused camera gear in the closet.",
];

/**
 * Sarcastic warning messages for unused function
 */
const UNUSED_FUNCTION_MESSAGES = [
  "Warning: Function '{name}' is never called. It's as neglected as the CTO's photo editing queue.",
  "Mon ami, fonction '{name}' exists but nobody calls it. Like good composition advice to the CTO.",
  "Warning: '{name}' is defined but never used. It has the utility of a tripod left in the car.",
  "Sacre bleu! Unused function '{name}'. More dead code than the CTO's finished photo projects.",
];

/**
 * Sarcastic warning messages for unreachable code
 */
const UNREACHABLE_CODE_MESSAGES = [
  "Warning: Unreachable code detected. This will never execute, like the CTO's photo organization plans.",
  "Mon ami, this code is unreachable. Like sharp focus in a moving chairlift selfie.",
  "Warning: Dead code ahead. It's as accessible as the CTO during a powder day.",
  "Sacre bleu! Unreachable code. Your logic is as blocked as good lighting at noon.",
];

/**
 * Sarcastic warning messages for deprecated feature
 */
const DEPRECATED_FEATURE_MESSAGES = [
  "Warning: This feature is deprecated. It's as outdated as the CTO's first digital camera.",
  "Mon ami, this is deprecated. Like using a flip phone to photograph ski runs.",
  "Warning: Deprecated! This is as obsolete as film cameras... wait, those are cool again. Never mind.",
  "Sacre bleu! Deprecated feature. Time to upgrade, like the CTO always says about camera bodies.",
];

/**
 * Sarcastic warning messages for shadowed variable
 */
const SHADOWED_VARIABLE_MESSAGES = [
  "Warning: Variable '{name}' shadows an outer scope variable. Confusing, like the CTO's photo filing system.",
  "Mon ami, '{name}' is shadowed. Like good lighting shadowed by poor camera settings.",
  "Warning: '{name}' shadows another '{name}'. Double naming, double confusion, double leg day.",
  "Sacre bleu! Variable shadowing. Your scopes are as messy as an unorganized photo library.",
];

/**
 * Sarcastic warning messages for missing await
 */
const MISSING_AWAIT_MESSAGES = [
  "Warning: Missing 'attendre' on async call. Your promise floats unresolved, like the CTO's to-do list.",
  "Mon ami, you forgot to await! This promise is as ignored as photo metadata.",
  "Warning: Async call without attendre. Like shooting without checking camera settings first.",
  "Sacre bleu! Missing await. Your async code is as unfinished as 'I'll caption these later'.",
];

/**
 * Sarcastic warning messages for implicit any
 */
const IMPLICIT_ANY_MESSAGES = [
  "Warning: Variable '{name}' has implicit type. As vague as 'we'll leave at 7-ish for the slopes'.",
  "Mon ami, '{name}' could be anything. Like asking what gear the CTO is bringing - who knows!",
  "Warning: Implicit any type. Your types are as undefined as the CTO's plans after 'one quick run'.",
  "Sacre bleu! Type unclear. This variable is as mysterious as why all action shots are blurry.",
];

/**
 * Sarcastic warning messages for loose equality
 */
const LOOSE_EQUALITY_MESSAGES = [
  "Warning: Consider using 'est' instead of '=='. Strict equality, like strict focus requirements.",
  "Mon ami, loose equality detected. Be precise! Unlike the CTO's autofocus in low light.",
  "Warning: '==' used instead of 'est'. Your comparisons are as loose as the CTO's definition of 'sharp'.",
  "Sacre bleu! Loose equality. Precision matters, like when not photographing moving targets. Wait...",
];

/**
 * Sarcastic warning messages for empty block
 */
const EMPTY_BLOCK_MESSAGES = [
  "Warning: Empty block statement. It does nothing, like the CTO's photo backup strategy.",
  "Mon ami, empty block detected. As useful as an unloaded camera on a ski trip.",
  "Warning: This block is empty. Like the CTO's 'edited photos' folder.",
  "Sacre bleu! Empty block. Your code has the same content as properly exposed night shots - none.",
];

/**
 * Sarcastic warning messages for suspicious assignment
 */
const SUSPICIOUS_ASSIGNMENT_MESSAGES = [
  "Warning: Did you mean 'est' (===) instead of '=' here? Assignment in condition looks suspicious.",
  "Mon ami, assignment in condition? This seems wrong, like using a GoPro for portraits.",
  "Warning: Suspicious assignment. Are you sure you meant '=' and not 'est'? Even the CTO double-checks... sometimes.",
  "Sacre bleu! Assignment in boolean context. Your conditionals are as confused as ISO settings at night.",
];

/**
 * Error message collections by type (Lexer)
 */
const LEXER_ERROR_MESSAGES: Record<LexerErrorType, string[]> = {
  [LexerErrorType.UNTERMINATED_STRING]: UNTERMINATED_STRING_MESSAGES,
  [LexerErrorType.UNTERMINATED_COMMENT]: UNTERMINATED_COMMENT_MESSAGES,
  [LexerErrorType.INVALID_CHARACTER]: INVALID_CHARACTER_MESSAGES,
  [LexerErrorType.UNEXPECTED_TOKEN]: UNEXPECTED_TOKEN_MESSAGES,
};

/**
 * Error message collections by type (Parser)
 */
const PARSER_ERROR_MESSAGES: Record<ParserErrorType, string[]> = {
  [ParserErrorType.MISSING_SEMICOLON]: MISSING_SEMICOLON_MESSAGES,
  [ParserErrorType.MISSING_CLOSING_PAREN]: MISSING_CLOSING_PAREN_MESSAGES,
  [ParserErrorType.MISSING_CLOSING_BRACE]: MISSING_CLOSING_BRACE_MESSAGES,
  [ParserErrorType.INVALID_ASSIGNMENT_TARGET]: INVALID_ASSIGNMENT_TARGET_MESSAGES,
  [ParserErrorType.EXPECTED_EXPRESSION]: EXPECTED_EXPRESSION_MESSAGES,
  [ParserErrorType.EXPECTED_IDENTIFIER]: EXPECTED_IDENTIFIER_MESSAGES,
  [ParserErrorType.EXPECTED_FUNCTION_NAME]: EXPECTED_FUNCTION_NAME_MESSAGES,
  [ParserErrorType.EXPECTED_PARAMETER_NAME]: EXPECTED_PARAMETER_NAME_MESSAGES,
  [ParserErrorType.EXPECTED_OPENING_PAREN]: EXPECTED_OPENING_PAREN_MESSAGES,
  [ParserErrorType.EXPECTED_OPENING_BRACE]: EXPECTED_OPENING_BRACE_MESSAGES,
  [ParserErrorType.UNKNOWN_STATEMENT]: UNKNOWN_STATEMENT_MESSAGES,
};

/**
 * Error message collections by type (Type Errors)
 */
const TYPE_ERROR_MESSAGES: Record<TypeErrorType, string[]> = {
  [TypeErrorType.TYPE_MISMATCH]: TYPE_MISMATCH_MESSAGES,
  [TypeErrorType.UNDEFINED_VARIABLE]: UNDEFINED_VARIABLE_MESSAGES,
  [TypeErrorType.NOT_A_FUNCTION]: NOT_A_FUNCTION_MESSAGES,
  [TypeErrorType.WRONG_ARGUMENT_COUNT]: WRONG_ARGUMENT_COUNT_MESSAGES,
  [TypeErrorType.CANNOT_ASSIGN_TO_CONST]: CANNOT_ASSIGN_TO_CONST_MESSAGES,
  [TypeErrorType.INVALID_OPERATION]: INVALID_OPERATION_MESSAGES,
  [TypeErrorType.NOT_AN_ARRAY]: NOT_AN_ARRAY_MESSAGES,
  [TypeErrorType.NOT_AN_OBJECT]: NOT_AN_OBJECT_MESSAGES,
  [TypeErrorType.PROPERTY_NOT_FOUND]: PROPERTY_NOT_FOUND_MESSAGES,
  [TypeErrorType.INVALID_INDEX]: INVALID_INDEX_MESSAGES,
  [TypeErrorType.NULL_REFERENCE]: NULL_REFERENCE_MESSAGES,
  [TypeErrorType.INVALID_RETURN_TYPE]: INVALID_RETURN_TYPE_MESSAGES,
  [TypeErrorType.MISSING_RETURN]: MISSING_RETURN_MESSAGES,
  [TypeErrorType.INVALID_OPERAND]: INVALID_OPERAND_MESSAGES,
  [TypeErrorType.CANNOT_ITERATE]: CANNOT_ITERATE_MESSAGES,
};

/**
 * Error message collections by type (Runtime Errors)
 */
const RUNTIME_ERROR_MESSAGES: Record<RuntimeErrorType, string[]> = {
  [RuntimeErrorType.DIVISION_BY_ZERO]: DIVISION_BY_ZERO_MESSAGES,
  [RuntimeErrorType.STACK_OVERFLOW]: STACK_OVERFLOW_MESSAGES,
  [RuntimeErrorType.INFINITE_LOOP]: INFINITE_LOOP_MESSAGES,
  [RuntimeErrorType.OUT_OF_MEMORY]: OUT_OF_MEMORY_MESSAGES,
  [RuntimeErrorType.TIMEOUT]: TIMEOUT_MESSAGES,
  [RuntimeErrorType.ASSERTION_FAILED]: ASSERTION_FAILED_MESSAGES,
  [RuntimeErrorType.UNHANDLED_EXCEPTION]: UNHANDLED_EXCEPTION_MESSAGES,
  [RuntimeErrorType.INDEX_OUT_OF_BOUNDS]: INDEX_OUT_OF_BOUNDS_MESSAGES,
  [RuntimeErrorType.RECURSION_LIMIT]: RECURSION_LIMIT_MESSAGES,
  [RuntimeErrorType.ASYNC_ERROR]: ASYNC_ERROR_MESSAGES,
};

/**
 * Warning message collections by type
 */
const WARNING_MESSAGES: Record<WarningType, string[]> = {
  [WarningType.UNUSED_VARIABLE]: UNUSED_VARIABLE_MESSAGES,
  [WarningType.UNUSED_FUNCTION]: UNUSED_FUNCTION_MESSAGES,
  [WarningType.UNREACHABLE_CODE]: UNREACHABLE_CODE_MESSAGES,
  [WarningType.DEPRECATED_FEATURE]: DEPRECATED_FEATURE_MESSAGES,
  [WarningType.SHADOWED_VARIABLE]: SHADOWED_VARIABLE_MESSAGES,
  [WarningType.MISSING_AWAIT]: MISSING_AWAIT_MESSAGES,
  [WarningType.IMPLICIT_ANY]: IMPLICIT_ANY_MESSAGES,
  [WarningType.LOOSE_EQUALITY]: LOOSE_EQUALITY_MESSAGES,
  [WarningType.EMPTY_BLOCK]: EMPTY_BLOCK_MESSAGES,
  [WarningType.SUSPICIOUS_ASSIGNMENT]: SUSPICIOUS_ASSIGNMENT_MESSAGES,
};

/**
 * Gets a random lexer error message for the given error type
 * @param type The type of lexer error
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getLexerErrorMessage(
  type: LexerErrorType,
  substitutions?: Record<string, string>
): string {
  const messages = LEXER_ERROR_MESSAGES[type];
  const index = Math.floor(Math.random() * messages.length);
  let message = messages[index];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a random parser error message for the given error type
 * @param type The type of parser error
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getParserErrorMessage(
  type: ParserErrorType,
  substitutions?: Record<string, string>
): string {
  const messages = PARSER_ERROR_MESSAGES[type];
  const index = Math.floor(Math.random() * messages.length);
  let message = messages[index];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a random error message for the given error type (legacy, for lexer)
 * @param type The type of error
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getErrorMessage(
  type: LexerErrorType,
  substitutions?: Record<string, string>
): string {
  return getLexerErrorMessage(type, substitutions);
}

/**
 * Gets a specific lexer error message (for deterministic testing)
 * @param type The type of lexer error
 * @param index The index of the message to retrieve
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getLexerErrorMessageAt(
  type: LexerErrorType,
  index: number,
  substitutions?: Record<string, string>
): string {
  const messages = LEXER_ERROR_MESSAGES[type];
  const safeIndex = index % messages.length;
  let message = messages[safeIndex];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a specific parser error message (for deterministic testing)
 * @param type The type of parser error
 * @param index The index of the message to retrieve
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getParserErrorMessageAt(
  type: ParserErrorType,
  index: number,
  substitutions?: Record<string, string>
): string {
  const messages = PARSER_ERROR_MESSAGES[type];
  const safeIndex = index % messages.length;
  let message = messages[safeIndex];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a specific error message (legacy, for lexer - for deterministic testing)
 * @param type The type of error
 * @param index The index of the message to retrieve
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getErrorMessageAt(
  type: LexerErrorType,
  index: number,
  substitutions?: Record<string, string>
): string {
  return getLexerErrorMessageAt(type, index, substitutions);
}

/**
 * Gets a random type error message for the given error type
 * @param type The type of type error
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getTypeErrorMessage(
  type: TypeErrorType,
  substitutions?: Record<string, string>
): string {
  const messages = TYPE_ERROR_MESSAGES[type];
  const index = Math.floor(Math.random() * messages.length);
  let message = messages[index];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a specific type error message (for deterministic testing)
 * @param type The type of type error
 * @param index The index of the message to retrieve
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getTypeErrorMessageAt(
  type: TypeErrorType,
  index: number,
  substitutions?: Record<string, string>
): string {
  const messages = TYPE_ERROR_MESSAGES[type];
  const safeIndex = index % messages.length;
  let message = messages[safeIndex];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a random runtime error message for the given error type
 * @param type The type of runtime error
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getRuntimeErrorMessage(
  type: RuntimeErrorType,
  substitutions?: Record<string, string>
): string {
  const messages = RUNTIME_ERROR_MESSAGES[type];
  const index = Math.floor(Math.random() * messages.length);
  let message = messages[index];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a specific runtime error message (for deterministic testing)
 * @param type The type of runtime error
 * @param index The index of the message to retrieve
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic error message
 */
export function getRuntimeErrorMessageAt(
  type: RuntimeErrorType,
  index: number,
  substitutions?: Record<string, string>
): string {
  const messages = RUNTIME_ERROR_MESSAGES[type];
  const safeIndex = index % messages.length;
  let message = messages[safeIndex];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a random warning message for the given warning type
 * @param type The type of warning
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic warning message
 */
export function getWarningMessage(
  type: WarningType,
  substitutions?: Record<string, string>
): string {
  const messages = WARNING_MESSAGES[type];
  const index = Math.floor(Math.random() * messages.length);
  let message = messages[index];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a specific warning message (for deterministic testing)
 * @param type The type of warning
 * @param index The index of the message to retrieve
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A sarcastic warning message
 */
export function getWarningMessageAt(
  type: WarningType,
  index: number,
  substitutions?: Record<string, string>
): string {
  const messages = WARNING_MESSAGES[type];
  const safeIndex = index % messages.length;
  let message = messages[safeIndex];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Gets a random message from any error category for maximum variety
 * This function randomly selects from syntax, type, runtime, or warning messages
 * @param category The error category to select from
 * @param substitutions Optional key-value pairs to substitute in the message
 * @returns A random sarcastic message from that category
 */
export function getRandomMessageFromCategory(
  category: ErrorCategory,
  substitutions?: Record<string, string>
): string {
  let allMessages: string[] = [];

  switch (category) {
    case ErrorCategory.SYNTAX:
      // Combine lexer and parser messages for syntax
      allMessages = [
        ...UNTERMINATED_STRING_MESSAGES,
        ...UNTERMINATED_COMMENT_MESSAGES,
        ...INVALID_CHARACTER_MESSAGES,
        ...UNEXPECTED_TOKEN_MESSAGES,
        ...MISSING_SEMICOLON_MESSAGES,
        ...MISSING_CLOSING_PAREN_MESSAGES,
        ...MISSING_CLOSING_BRACE_MESSAGES,
        ...EXPECTED_EXPRESSION_MESSAGES,
        ...UNKNOWN_STATEMENT_MESSAGES,
      ];
      break;
    case ErrorCategory.TYPE:
      allMessages = Object.values(TYPE_ERROR_MESSAGES).flat();
      break;
    case ErrorCategory.RUNTIME:
      allMessages = Object.values(RUNTIME_ERROR_MESSAGES).flat();
      break;
    case ErrorCategory.WARNING:
      allMessages = Object.values(WARNING_MESSAGES).flat();
      break;
    case ErrorCategory.REFERENCE:
      // Reference errors are subset of type errors
      allMessages = [
        ...UNDEFINED_VARIABLE_MESSAGES,
        ...PROPERTY_NOT_FOUND_MESSAGES,
      ];
      break;
  }

  const index = Math.floor(Math.random() * allMessages.length);
  let message = allMessages[index];

  // Apply substitutions
  if (substitutions) {
    for (const [key, value] of Object.entries(substitutions)) {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  }

  return message;
}

/**
 * Formats an error with line number
 * @param message The error message
 * @param line The line number where the error occurred
 * @returns Formatted error message with line number
 */
export function formatErrorWithLine(message: string, line: number): string {
  return `[Ligne ${line}] ${message}`;
}

/**
 * Template function for inserting line numbers, names, and other context into error messages
 * @param template The message template with placeholders like {name}, {line}, {expected}, etc.
 * @param context Object containing values to substitute
 * @returns Formatted error message with context inserted
 */
export function formatErrorTemplate(
  template: string,
  context: {
    name?: string;
    line?: number;
    expected?: string;
    actual?: string;
    type?: string;
    property?: string;
    operator?: string;
    operation?: string;
    index?: number;
    keyword?: string;
  }
): string {
  let message = template;

  if (context.name !== undefined) {
    message = message.replace(/\{name\}/g, context.name);
  }
  if (context.line !== undefined) {
    message = message.replace(/\{line\}/g, String(context.line));
  }
  if (context.expected !== undefined) {
    message = message.replace(/\{expected\}/g, context.expected);
  }
  if (context.actual !== undefined) {
    message = message.replace(/\{actual\}/g, context.actual);
  }
  if (context.type !== undefined) {
    message = message.replace(/\{type\}/g, context.type);
  }
  if (context.property !== undefined) {
    message = message.replace(/\{property\}/g, context.property);
  }
  if (context.operator !== undefined) {
    message = message.replace(/\{operator\}/g, context.operator);
  }
  if (context.operation !== undefined) {
    message = message.replace(/\{operation\}/g, context.operation);
  }
  if (context.index !== undefined) {
    message = message.replace(/\{index\}/g, String(context.index));
  }
  if (context.keyword !== undefined) {
    message = message.replace(/\{keyword\}/g, context.keyword);
  }

  return message;
}

/**
 * Creates a fully formatted error with category, line number, and context
 * @param category The error category
 * @param message The error message
 * @param line The line number
 * @returns Fully formatted error string
 */
export function createFormattedError(
  category: ErrorCategory,
  message: string,
  line: number
): string {
  const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
  return `[${categoryLabel}Error] [Ligne ${line}] ${message}`;
}

/**
 * Gets the total count of error messages by category
 * Useful for testing and documentation
 */
export function getMessageCounts(): {
  syntax: number;
  type: number;
  runtime: number;
  warning: number;
  total: number;
} {
  const syntaxCount =
    Object.values(LEXER_ERROR_MESSAGES).flat().length +
    Object.values(PARSER_ERROR_MESSAGES).flat().length;
  const typeCount = Object.values(TYPE_ERROR_MESSAGES).flat().length;
  const runtimeCount = Object.values(RUNTIME_ERROR_MESSAGES).flat().length;
  const warningCount = Object.values(WARNING_MESSAGES).flat().length;

  return {
    syntax: syntaxCount,
    type: typeCount,
    runtime: runtimeCount,
    warning: warningCount,
    total: syntaxCount + typeCount + runtimeCount + warningCount,
  };
}
